import { authHeaders, handleUnauthorized } from './http';
const BASE = '/api';

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(init?.headers || {}) },
    ...init,
  });
  handleUnauthorized(res);
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || res.statusText);
  }
  return res.json();
}

import type {
  AnalystAssumption,
  Dashboard,
  DataGap,
  EvidenceItem,
  ImportResult,
  InteractionEffect,
  LagProfile,
  ObservationsSummary,
  PolicyArea,
  ScenarioResult,
  ScenarioSnapshot,
  SessionState,
  Variable,
  VettingItem,
} from '../types';

export const api = {
  health: () => request<{ status: string }>('/health'),
  dashboard: () => request<Dashboard>('/dashboard'),
  banner: () => request<Dashboard['banner']>('/banner'),
  session: () => request<SessionState>('/session'),
  scenarios: () => request<string[]>('/scenarios'),
  selectScenario: (scenario: string) =>
    request('/scenarios/select', { method: 'POST', body: JSON.stringify({ scenario }) }),
  allResults: () => request<ScenarioResult[]>('/scenarios/results'),
  resetCustom: () => request('/scenarios/custom/reset', { method: 'POST' }),
  setOverride: (variable_id: string, value: number, note?: string) =>
    request('/scenarios/custom/override', {
      method: 'POST',
      body: JSON.stringify({ variable_id, value, note }),
    }),
  setInteractionIntensity: (intensity: number) =>
    request('/scenarios/custom', {
      method: 'POST',
      body: JSON.stringify({ overrides: [], interaction_intensity: intensity }),
    }),
  snapshots: () => request<ScenarioSnapshot[]>('/scenarios/snapshots'),
  saveSnapshot: (name: string, note?: string) =>
    request<ScenarioSnapshot>('/scenarios/snapshots', {
      method: 'POST',
      body: JSON.stringify({ name, note }),
    }),
  loadSnapshot: (id: string) =>
    request<SessionState>(`/scenarios/snapshots/${id}/load`, { method: 'POST' }),
  deleteSnapshot: (id: string) =>
    request(`/scenarios/snapshots/${id}`, { method: 'DELETE' }),
  auditLog: (limit = 100) => request<AnalystAssumption[]>(`/analyst-assumptions?limit=${limit}`),
  policyAreas: () => request<PolicyArea[]>('/policy-areas'),
  areaVariables: (areaId: string) => request<Variable[]>(`/policy-areas/${areaId}/variables`),
  gdpDrivers: () => request<Variable[]>('/variables/gdp-drivers'),
  variable: (id: string) => request<Variable>(`/variables/${id}`),
  variableContribution: (id: string) => request(`/variables/${id}/contribution`),
  decomposition: () => request<ScenarioResult>('/decomposition'),
  vetting: () => request<VettingItem[]>('/vetting'),
  interactions: () => request<InteractionEffect[]>('/interactions'),
  lags: () => request<LagProfile[]>('/lags'),
  elasticities: () => request<EvidenceItem[]>('/evidence/elasticities'),
  dataGaps: () => request<DataGap[]>('/evidence/gaps'),
  observationsSummary: () => request<ObservationsSummary>('/observations/summary'),
  importObservations: async (file: File): Promise<ImportResult> => {
    const form = new FormData();
    form.append('file', file);
    const res = await fetch(`${BASE}/import/observations`, { method: 'POST', body: form, headers: authHeaders() });
    handleUnauthorized(res);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },
};
